import React from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Mail, ArrowRight, Instagram, Facebook, Star } from "lucide-react";
import { Button } from "./ui/button";
import { COMPANY } from "../data/mock";
import { POPULAR } from "../data/cities";
import { useLang } from "../i18n/LanguageContext";
import { useLocalizedPath, cityPath, localize } from "../i18n/langPath";
import { BrandLogo } from "./BrandLogo";

export default function Footer() {
  const { t, lang } = useLang();
  const lp = useLocalizedPath();
  const services = [
    { label: t("ft_svc_flughafen"), to: "/blog/flughafentransfer-schweiz" },
    { label: t("ft_svc_city"), to: "/blog/stadt-zu-stadt-transfer" },
    { label: t("ft_svc_hourly"), to: "/blog/stundenbuchung-chauffeur" },
    { label: t("ft_svc_biz"), to: "/blog/geschaeftsreisen-chauffeur" },
  ];
  const company = [
    { label: t("ft_fleet"), to: "/#fleet" },
    { label: t("ft_dest"), to: "/staedte" },
    { label: t("ft_blog"), to: "/blog" },
    { label: t("ft_pickup"), to: lang === "en" ? "/airport-pickup-guide" : "/flughafen-abholung" },
    { label: t("ft_about"), to: "/about" },
    { label: t("nav_status"), to: "/status" },
    { label: t("ft_contact"), to: "/kontakt" },
  ];
  const routes = lang === "en"
    ? [
        { label: "Zurich Airport Taxi", to: "/zurich-airport-taxi" },
        { label: "Zurich Airport Transfer", to: "/flughafentransfer-zuerich" },
        { label: "Flughafentaxi Zürich", to: "/flughafentaxi-zuerich" },
        { label: "Zurich Airport to Zurich transfer", to: "/zurich-airport-to-zurich" },
        { label: "Zurich Airport to Lucerne transfer", to: "/zurich-airport-to-lucerne" },
        { label: "Zurich Airport to Interlaken transfer", to: "/zurich-airport-to-interlaken" },
        { label: "Zurich Airport to Davos transfer", to: "/zurich-airport-to-davos" },
        { label: "WEF 2027 transfer to Davos", to: "/wef-2027" },
        { label: "Ski transfer Switzerland", to: "/ski-transfer-schweiz" },
        { label: "Zurich Airport to Geneva transfer", to: "/zurich-airport-to-geneva" },
      ]
    : lang === "ckb"
    ? [
        { label: "گواستنەوەی فڕۆکەخانەی زوریخ", to: "/flughafentransfer-zuerich" },
        { label: "تاکسی فڕۆکەخانەی زوریخ", to: "/flughafentaxi-zuerich" },
        { label: "تاکسی فڕۆکەخانەی زوریخ (ZRH)", to: "/zurich-airport-taxi" },
        { label: "فڕۆکەخانەی زوریخ بۆ زوریخ", to: "/zurich-airport-to-zurich" },
        { label: "فڕۆکەخانەی زوریخ بۆ لوسێرن", to: "/zurich-airport-to-lucerne" },
        { label: "فڕۆکەخانەی زوریخ بۆ ئینتەرلاکن", to: "/zurich-airport-to-interlaken" },
        { label: "فڕۆکەخانەی زوریخ بۆ داڤۆس", to: "/zurich-airport-to-davos" },
        { label: "گواستنەوەی WEF 2027 بۆ داڤۆس", to: "/wef-2027" },
        { label: "گواستنەوەی سکی لە سویسرا", to: "/ski-transfer-schweiz" },
      ]
    : [
        { label: "Flughafentransfer Zürich", to: "/flughafentransfer-zuerich" },
        { label: "Flughafentaxi Zürich", to: "/flughafentaxi-zuerich" },
        { label: "Zurich Airport Taxi", to: "/zurich-airport-taxi" },
        { label: "Flughafen Zürich nach Zürich", to: "/zurich-airport-to-zurich" },
        { label: "Flughafen Zürich nach Luzern", to: "/zurich-airport-to-lucerne" },
        { label: "Flughafen Zürich nach Interlaken", to: "/zurich-airport-to-interlaken" },
        { label: "Flughafen Zürich nach Davos", to: "/zurich-airport-to-davos" },
        { label: "WEF 2027 Transfer Davos", to: "/wef-2027" },
        { label: "Ski Transfer Schweiz", to: "/ski-transfer-schweiz" },
        { label: "Flughafentaxi Zug", to: "/flughafentaxi-zug" },
        { label: "Flughafen Zürich nach Genf", to: "/zurich-airport-to-geneva" },
      ];
  const legalLinks = lang === "en"
    ? [["/imprint", "Imprint"], ["/privacy", "Privacy Policy"], ["/terms", "Terms"], ["/cancellation", "Cancellation"]]
    : lang === "ckb"
    ? [["/impressum", "ئیمپرینت"], ["/datenschutz", "پاراستنی داتا"], ["/agb", "مەرج و ڕێساکان"], ["/stornierung", "هەڵوەشاندنەوە"]]
    : [["/impressum", "Impressum"], ["/datenschutz", "Datenschutz"], ["/agb", "AGB"], ["/stornierung", "Stornierung"]];
  return (
    <footer id="contact" className="relative bg-white border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
        <div className="rounded-3xl bg-black p-10 md:p-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="max-w-xl">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-white leading-tight">{t("footer_cta_title")}</h2>
            <p className="mt-3 text-slate-300">{t("footer_cta_sub")}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to={lp("/booking")}><Button className="bg-[#F5B400] hover:bg-[#D99A00] text-neutral-900 rounded-full px-7 h-12 font-semibold" data-testid="footer-book-btn">{t("book_now")} <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <a href={`https://wa.me/${COMPANY.whatsapp}`} target="_blank" rel="noreferrer"><Button variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white rounded-full px-7 h-12 font-semibold"><MessageCircle className="w-4 h-4 mr-2" /> WhatsApp</Button></a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 pb-14">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10 border-t border-slate-100 pt-12">
          <div className="col-span-2">
            <span data-testid="footer-logo">
              <BrandLogo />
            </span>
            <p className="mt-4 text-slate-500 text-sm max-w-xs">{t("ft_desc")}</p>
            <div className="mt-5 space-y-2 text-sm">
              <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-2 text-slate-600 hover:text-[#F5B400]"><Phone className="w-4 h-4" /> {COMPANY.phone}</a>
              <a href={`tel:${COMPANY.officePhoneRaw}`} className="flex items-center gap-2 text-slate-600 hover:text-[#F5B400]" data-testid="footer-office-phone"><Phone className="w-4 h-4" /> {COMPANY.officePhone}</a>
              <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 text-slate-600 hover:text-[#F5B400]"><Mail className="w-4 h-4" /> {COMPANY.email}</a>
            </div>
            {/* Social & review — 4-button system */}
            <div className="mt-5 flex items-center gap-3" data-testid="footer-social-buttons">
              <a href="https://www.instagram.com/suisselane" target="_blank" rel="noreferrer" aria-label="Instagram" title="Instagram" data-testid="footer-instagram"
                className="w-11 h-11 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:text-white hover:bg-[#E1306C] hover:border-[#E1306C] transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://www.facebook.com/share/195Bu7cMfT/" target="_blank" rel="noreferrer" aria-label="Facebook" title="Facebook" data-testid="footer-facebook"
                className="w-11 h-11 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:text-white hover:bg-[#1877F2] hover:border-[#1877F2] transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href={`https://wa.me/${COMPANY.phoneRaw.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" aria-label="WhatsApp" title="WhatsApp" data-testid="footer-whatsapp"
                className="w-11 h-11 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:text-white hover:bg-[#25D366] hover:border-[#25D366] transition-colors">
                <MessageCircle className="w-5 h-5" />
              </a>
              <a href="https://www.google.com/maps?cid=8059183557833479690" target="_blank" rel="noreferrer" aria-label="Google" title={lang === "en" ? "Rate us on Google" : lang === "ckb" ? "لە گووگڵ هەڵسەنگاندنمان بکە" : "Auf Google bewerten"} data-testid="footer-google-review"
                className="w-11 h-11 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:text-white hover:bg-[#F5B400] hover:border-[#F5B400] transition-colors">
                <Star className="w-5 h-5 fill-current" />
              </a>
            </div>
          </div>
          <div>
            <h4 className="text-black font-semibold mb-4 text-sm">{t("ft_services")}</h4>
            <ul className="space-y-2.5">
              {services.map((s) => <li key={s.label}><Link to={lp(s.to)} className="text-slate-500 text-sm hover:text-[#F5B400]">{s.label}</Link></li>)}
            </ul>
          </div>
          <div>
            <h4 className="text-black font-semibold mb-4 text-sm">{t("ft_company")}</h4>
            <ul className="space-y-2.5">
              {company.map((c) => <li key={c.label}><Link to={lp(c.to)} className="text-slate-500 text-sm hover:text-[#F5B400]">{c.label}</Link></li>)}
            </ul>
          </div>
          <div>
            <h4 className="text-black font-semibold mb-4 text-sm">{t("ft_popular")}</h4>
            <ul className="space-y-2.5">
              {POPULAR.slice(0, 6).map((c) => <li key={c.slug}><Link to={cityPath(c.slug, lang)} className="text-slate-500 text-sm hover:text-[#F5B400]">{c.name}</Link></li>)}
            </ul>
          </div>
        </div>
        <div className="mt-10 border-t border-slate-100 pt-8">
          <h4 className="text-black font-semibold mb-4 text-sm">{lang === "en" ? "Popular Zurich Airport routes" : lang === "ckb" ? "ڕێڕەوە بەناوبانگەکان لە فڕۆکەخانەی زوریخ" : "Beliebte Strecken ab Flughafen Zürich"}</h4>
          <ul className="flex flex-wrap gap-x-6 gap-y-2" data-testid="footer-routes">
            {routes.map((r) => (
              <li key={r.to}><Link to={localize(r.to, lang === "de" ? "de" : "en")} className="text-slate-500 text-sm hover:text-[#F5B400]">{r.label}</Link></li>
            ))}
          </ul>
        </div>
        <div className="mt-12 pt-10 border-t border-slate-100">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-14">
            <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_suisse-lane/artifacts/vtadaa7a_IMG_2919.png" alt="Swiss Made" className="h-16 w-16 object-contain" data-testid="footer-swissmade" />
            <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_suisse-lane/artifacts/ancjq8li_IMG_2880.png" alt="Qualität 100% – Zahlungsarten Maestro, MasterCard, PostFinance, Visa, American Express" className="h-28 object-contain" data-testid="footer-payments" />
            <a href="https://ch.trustpilot.com/evaluate/zuerichairporttaxi.ch" target="_blank" rel="noreferrer" className="flex flex-col items-center gap-2 group" data-testid="footer-trustpilot-review">
              <img src="https://customer-assets.emergentagent.com/job_suisse-lane/artifacts/3sgfngzf_IMG_2634.jpeg" alt="Trustpilot – Rated Excellent" className="h-24 object-contain rounded-xl transition-transform group-hover:scale-105" data-testid="footer-trustpilot" />
              <span className="text-xs font-semibold text-[#00b67a] group-hover:underline">{lang === "en" ? "Write a review on Trustpilot" : lang === "ckb" ? "هەڵسەنگاندنێک لە Trustpilot بنووسە" : "Bewertung auf Trustpilot abgeben"}</span>
            </a>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-slate-500 text-sm">© {new Date().getFullYear()} Zuerich Airport Taxi. {t("ft_rights")}</p>
          <ul className="flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="footer-legal-links">
            {legalLinks.map(([to, label]) => (
              <li key={to}><Link to={lp(to)} className="text-slate-400 text-xs hover:text-[#F5B400]">{label}</Link></li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}
